import { ChargeSessionCodeType } from '../../../../enums';

const ASSESMENT_KEY = 'ASSESSMENT';
const ROLE_KEY = 'ROLE';

const categoryToOriginalCodeAssessment = {
  CORRECT: true,
  INCORRECT: true,
  MISSED: false
};

// extracts a list of values from Row Values from CMxAPI.RowData
function GetOptionsFromRowValues(
  rowData: { [key: string]: string }[],
  key: string
) {
  // reduce is favored over map because we want to skip undefined values
  return rowData.reduce((acc, row) => {
    const value = row[key];
    if (value !== undefined && !acc.includes(value)) {
      acc.push(value);
    }
    return acc;
  }, [] as string[]);
}

function updateFieldOptionsWithObject(fields, optionsObject) {
  return fields.map(field => {
    const matchingOption = optionsObject.find(
      option => option.key.toLowerCase() === field.key.toLowerCase()
    );
    if (matchingOption) {
      return {
        ...field,
        options: matchingOption.options,
        optionsTree: matchingOption.optionsTree
      };
    }
    return field;
  });
}

type ProcessedRiskCategories = {
  fields: {
    cpt: CMxCommonApp.CodeLayerField[];
    icd: CMxCommonApp.CodeLayerField[];
    modifier: CMxCommonApp.CodeLayerField[];
  };
  riskCategories: {
    [ChargeSessionCodeType.PROCEDURE]: {};
    [ChargeSessionCodeType.DIAGNOSIS]: {};
    [ChargeSessionCodeType.MODIFIER]: {};
  };
};

// Generic function to process simple risk categories (CPT and ICD)
function processSimpleRiskCategories(rowValues: { [key: string]: string }[]) {
  const riskCategories = {};
  rowValues.forEach(row => {
    if (!riskCategories[row['ASSESSMENT']]) {
      riskCategories[row['ASSESSMENT']] = [];
    }
    if (!riskCategories[row['ASSESSMENT']].includes(row['ROLE'])) {
      riskCategories[row['ASSESSMENT']].push(row['ROLE']);
    }
  });
  return riskCategories;
}

const getGetAssessmentTree = (rowValues: { [key: string]: string }[]) => {
  const assessmentTree = {};
  // Process assessment tree
  rowValues.forEach(row => {
    const category = row['CATEGORY'];

    const originalCode = categoryToOriginalCodeAssessment[category] ?? true;

    if (!assessmentTree[originalCode]) {
      assessmentTree[originalCode] = [];
    }

    if (!assessmentTree[originalCode].includes(row['ASSESSMENT'])) {
      assessmentTree[originalCode].push(row['ASSESSMENT']);
    }
  });

  return assessmentTree;
};

// Process CPT risk categories
function processCPTRiskCategories(rowData: CMxAPI.RowData) {
  return {
    riskCategories: processSimpleRiskCategories(rowData.rowValues),
    assessmentOptions: GetOptionsFromRowValues(
      rowData.rowValues,
      ASSESMENT_KEY
    ),
    roleOptions: GetOptionsFromRowValues(rowData.rowValues, ROLE_KEY),
    assessmentTree: getGetAssessmentTree(rowData.rowValues)
  };
}

// Process ICD risk categories
function processICDRiskCategories(rowData: CMxAPI.RowData) {
  return {
    riskCategories: processSimpleRiskCategories(rowData.rowValues),
    assessmentOptions: GetOptionsFromRowValues(
      rowData.rowValues,
      ASSESMENT_KEY
    ),
    roleOptions: GetOptionsFromRowValues(rowData.rowValues, ROLE_KEY),
    assessmentTree: getGetAssessmentTree(rowData.rowValues)
  };
}

// Process Modifier risk categories (more complex structure)
function processModifierRiskCategories(rowData: CMxAPI.RowData) {
  // Process modifier-specific risk categories
  const modifierRiskCategories = {};
  const modifierAssessmentTree = {};

  // Process risk categories
  rowData.rowValues.forEach(row => {
    if (!modifierRiskCategories[row['MODIFIER']]) {
      modifierRiskCategories[row['MODIFIER']] = {};
    }

    if (!modifierRiskCategories[row['MODIFIER']][row['ASSESSMENT']]) {
      modifierRiskCategories[row['MODIFIER']][row['ASSESSMENT']] = [];
    }

    if (
      !modifierRiskCategories[row['MODIFIER']][row['ASSESSMENT']].includes(
        row['ROLE']
      )
    ) {
      modifierRiskCategories[row['MODIFIER']][row['ASSESSMENT']].push(
        row['ROLE']
      );
    }
  });

  // Process assessment tree
  rowData.rowValues.forEach(row => {
    const category = row['CATEGORY'];

    const originalCode = categoryToOriginalCodeAssessment[category] ?? true;

    if (!modifierAssessmentTree[row['MODIFIER']]) {
      modifierAssessmentTree[row['MODIFIER']] = {};
    }

    if (!modifierAssessmentTree[row['MODIFIER']][originalCode]) {
      modifierAssessmentTree[row['MODIFIER']][originalCode] = [];
    }

    if (
      !modifierAssessmentTree[row['MODIFIER']][originalCode].includes(
        row['ASSESSMENT']
      )
    ) {
      modifierAssessmentTree[row['MODIFIER']][originalCode].push(
        row['ASSESSMENT']
      );
    }
  });

  return {
    riskCategories: modifierRiskCategories,
    assessmentTree: modifierAssessmentTree,
    assessmentOptions: GetOptionsFromRowValues(
      rowData.rowValues,
      ASSESMENT_KEY
    ),
    roleOptions: GetOptionsFromRowValues(rowData.rowValues, ROLE_KEY)
  };
}

export const processRiskCategories = (
  fieldDefinitions: Accelerate.GradeBenchPanelFieldDefinitonState,
  riskCategoryData: CMxAPI.RiskCategoryData
): ProcessedRiskCategories => {
  // Process each code type
  const cptResults = processCPTRiskCategories(riskCategoryData.CptRowData);
  const icdResults = processICDRiskCategories(riskCategoryData.IcdRowData);
  const modResults = processModifierRiskCategories(riskCategoryData.ModRowData);

  // Create options objects for field updates
  const modOptions = [
    {
      key: ROLE_KEY,
      options: modResults.roleOptions,
      optionsTree: modResults.riskCategories
    },
    {
      key: ASSESMENT_KEY,
      options: modResults.assessmentOptions,
      optionsTree: modResults.assessmentTree
    }
  ];

  const icdOptions = [
    {
      key: ROLE_KEY,
      options: icdResults.roleOptions,
      optionsTree: icdResults.riskCategories
    },
    {
      key: ASSESMENT_KEY,
      options: icdResults.assessmentOptions,
      optionsTree: icdResults.assessmentTree
    }
  ];

  const cptOptions = [
    {
      key: ROLE_KEY,
      options: cptResults.roleOptions,
      optionsTree: cptResults.riskCategories
    },
    {
      key: ASSESMENT_KEY,
      options: cptResults.assessmentOptions,
      optionsTree: cptResults.assessmentTree
    }
  ];

  // Update fields with new options
  const updateModFields = updateFieldOptionsWithObject(
    fieldDefinitions.modifierFields,
    modOptions
  );
  const updateIcdFields = updateFieldOptionsWithObject(
    fieldDefinitions.icdFields,
    icdOptions
  );
  const updateCptFields = updateFieldOptionsWithObject(
    fieldDefinitions.cptFields,
    cptOptions
  );

  return {
    fields: {
      cpt: updateCptFields,
      modifier: updateModFields,
      icd: updateIcdFields
    },
    riskCategories: {
      [ChargeSessionCodeType.PROCEDURE]: cptResults.riskCategories,
      [ChargeSessionCodeType.DIAGNOSIS]: icdResults.riskCategories,
      [ChargeSessionCodeType.MODIFIER]: modResults.riskCategories
    }
  };
};
